import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import React from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { AUTH_PRODUCT_NAME } from "./authProduct";

export type AuthHandoffVariant = "loading" | "success" | "error";

interface AuthHandoffScreenProps {
  variant?: AuthHandoffVariant;
  title: string;
  description?: string;
  /** Sobre a página atual (fundo translúcido) em vez de tela própria. */
  overlay?: boolean;
  /** Ações (botões/links) exibidas abaixo do texto — usado no estado de erro. */
  children?: React.ReactNode;
}

/**
 * Tela de transição das telas de autenticação (redirecionamento entre o portal e os produtos).
 * Mesmo visual nas três plataformas: ícone, título e descrição centralizados.
 */
export const AuthHandoffScreen: React.FC<AuthHandoffScreenProps> = ({
  variant = "loading",
  title,
  description,
  overlay,
  children,
}) => {
  const icon =
    variant === "success" ? (
      <CheckCircle2 className="h-6 w-6 text-emerald-600" aria-hidden="true" />
    ) : variant === "error" ? (
      <AlertCircle className="h-6 w-6 text-rose-600" aria-hidden="true" />
    ) : (
      <Loader2
        className="h-6 w-6 animate-spin text-purple-700"
        aria-hidden="true"
      />
    );

  const surface = overlay
    ? "fixed inset-0 z-50 animate-fade-in bg-slate-50/85 backdrop-blur-xs"
    : "min-h-screen w-full bg-slate-50";

  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      aria-live="assertive"
      className={`flex items-center justify-center ${surface}`}
    >
      <div className="mx-6 flex w-full max-w-sm flex-col items-center gap-3 text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-purple-700">
          {AUTH_PRODUCT_NAME}
        </p>
        {icon}
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        {description && (
          <p className="text-xs leading-relaxed text-slate-500">
            {description}
          </p>
        )}
        {children && <div className="w-full space-y-2.5 pt-3">{children}</div>}
      </div>
    </div>
  );
};

/** Redirecionamento de volta ao produto de origem (SSO / recuperação de senha). */
export const RedirectingScreen: React.FC = () => {
  const { t } = useTranslation();
  return (
    <AuthHandoffScreen
      title={t("Redirecionando...")}
      description={t("Você será levado de volta ao aplicativo de origem.")}
    />
  );
};
